import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import API from '../../../services/API'
import { GlobalContext } from '../../../App'

const blankConsumable = {
  sectionName: 'General',
  name: '',
  size: '',
  quantity: 1,
  noExpiry: false,
}

const blankAsset = {
  sectionName: 'General',
  category: '',
  subCategory: '',
  quantity: 1,
}

const typeOptions = ['Vehicle', 'Bag', 'Device', 'Crash Trolley', 'Other']

const isConsumableStarted = (item) =>
  String(item.name || '').trim() ||
  String(item.size || '').trim() ||
  String(item.quantity ?? '') !== '1' ||
  item.noExpiry ||
  String(item.sectionName || '').trim() !== 'General'

// An asset row the user has touched but left without a category. Untouched
// blank rows are the default state and are ignored silently.
const isAssetStarted = (asset) =>
  String(asset.category || '').trim() ||
  String(asset.subCategory || '').trim() ||
  String(asset.quantity ?? '') !== '1' ||
  String(asset.sectionName || '').trim() !== 'General'

const MakeReadyWizard = () => {
  const { setToast } = useContext(GlobalContext)
  const navigate = useNavigate()

  const [activeStep, setActiveStep] = useState(0)
  const [title, setTitle] = useState('')
  const [type, setType] = useState('Vehicle')
  const [consumables, setConsumables] = useState([blankConsumable])
  const [requiredAssets, setRequiredAssets] = useState([blankAsset])
  const [assetCategories, setAssetCategories] = useState({})
  const [assets, setAssets] = useState([])
  const [vehicles, setVehicles] = useState([])
  const [selectedAssets, setSelectedAssets] = useState([])
  const [selectedVehicles, setSelectedVehicles] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [confirmIncomplete, setConfirmIncomplete] = useState(false)

  const isVehicleList = type === 'Vehicle'

  useEffect(() => {
    API.get('/settings/makereadyassetoptions').then((res) => {
      if (res.status === 200) setAssetCategories(res.data || {})
    })
    API.get('/assets').then((res) => {
      if (res.status === 200) setAssets(res.data || [])
    })
    API.get('/vehicleList').then((res) => {
      if (res.status === 200) setVehicles(res.data || [])
    })
  }, [])

  const sections = useMemo(() => {
    const byName = new Map()

    consumables
      .filter((item) => String(item.name || '').trim())
      .forEach((item) => {
        const sectionName = item.sectionName || 'General'
        const section = byName.get(sectionName) || {
          sectionName,
          sectionType: '',
          requiredConsumables: [],
          requiredAssets: [],
        }
        section.requiredConsumables.push({
          name: item.name,
          size: item.size,
          quantity: Number(item.quantity) || 1,
          noExpiry: !!item.noExpiry,
        })
        byName.set(sectionName, section)
      })

    // A category with no sub-category is a wildcard ("any <category>") and
    // is kept with subCategory '' rather than dropped.
    requiredAssets
      .filter((asset) => String(asset.category || '').trim())
      .forEach((asset) => {
        const sectionName = asset.sectionName || 'General'
        const section = byName.get(sectionName) || {
          sectionName,
          sectionType: '',
          requiredConsumables: [],
          requiredAssets: [],
        }
        section.requiredAssets.push({
          category: asset.category,
          subCategory: String(asset.subCategory || '').trim(),
          quantity: Number(asset.quantity) || 1,
        })
        byName.set(sectionName, section)
      })

    return Array.from(byName.values())
  }, [consumables, requiredAssets])

  const sectionOptions = useMemo(() => {
    const names = [...consumables, ...requiredAssets]
      .map((item) => String(item.sectionName || '').trim())
      .filter(Boolean)

    return Array.from(new Set(['General', ...names])).sort((a, b) =>
      a.localeCompare(b),
    )
  }, [consumables, requiredAssets])

  const hasInvalidConsumables = useMemo(
    () =>
      consumables.some((item) => {
        return (
          isConsumableStarted(item) &&
          (!String(item.sectionName || '').trim() ||
            !String(item.name || '').trim())
        )
      }),
    [consumables],
  )

  // Asset rows that were started but have no category cannot be saved;
  // they are surfaced instead of vanishing on submit.
  const incompleteAssets = useMemo(
    () =>
      requiredAssets
        .map((asset, index) => ({ asset, index }))
        .filter(
          ({ asset }) =>
            isAssetStarted(asset) && !String(asset.category || '').trim(),
        ),
    [requiredAssets],
  )

  const wildcardAssets = useMemo(
    () =>
      requiredAssets.filter(
        (asset) =>
          String(asset.category || '').trim() &&
          !String(asset.subCategory || '').trim(),
      ),
    [requiredAssets],
  )

  const canContinue =
    activeStep === 0
      ? title.trim() && type.trim()
      : activeStep === 1
        ? sections.length > 0 && !hasInvalidConsumables
        : true

  const updateConsumable = (index, patch) => {
    setConsumables((prev) =>
      prev.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    )
  }

  const updateAsset = (index, patch) => {
    setRequiredAssets((prev) =>
      prev.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    )
  }

  const deleteConsumable = (index) => {
    setConsumables((prev) =>
      prev.length === 1
        ? [{ ...blankConsumable }]
        : prev.filter((_, i) => i !== index),
    )
  }

  const deleteAsset = (index) => {
    setRequiredAssets((prev) =>
      prev.length === 1
        ? [{ ...blankAsset }]
        : prev.filter((_, i) => i !== index),
    )
  }

  const submit = async () => {
    setConfirmIncomplete(false)
    setSubmitting(true)
    try {
      const appliesTo = (isVehicleList ? selectedVehicles : selectedAssets).map(
        (x) => x._id,
      )
      const loadListRes = await API.post('/settings/makereadyloadlist', {
        title,
        type,
        sections,
        requiredAssets: [],
        appliesTo,
      })

      if (loadListRes.status !== 200 || !loadListRes.data?._id) {
        setToast({
          message:
            loadListRes.data?.error || 'Unable to create Make Ready load list',
          severity: 'error',
        })
        return
      }

      setToast({ message: 'Make Ready setup complete' })
      navigate('/make-ready-overview')
    } catch (e) {
      console.error(e)
      setToast({ message: 'Unable to complete setup', severity: 'error' })
    } finally {
      setSubmitting(false)
    }
  }

  const handleComplete = () => {
    if (incompleteAssets.length > 0) {
      setConfirmIncomplete(true)
      return
    }
    submit()
  }

  return (
    <Stack gap={2}>
      <Typography variant="title2" fontSize="1.2rem">
        Setup wizard
      </Typography>
      <Stepper activeStep={activeStep}>
        {['Details', 'Requirements', 'Assign'].map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {activeStep === 0 && (
        <Stack component={Paper} padding={2} gap={2}>
          <TextField
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            label="Load List Name"
            helperText="Examples: Frontline Ambulance, Primary Response Bag, Resus Trolley"
            required
          />
          <Autocomplete
            value={type}
            onChange={(e, newValue) => setType(newValue || '')}
            options={typeOptions}
            freeSolo
            renderInput={(params) => (
              <TextField {...params} label="Type" required />
            )}
          />
        </Stack>
      )}

      {activeStep === 1 && (
        <Stack gap={2}>
          <Stack component={Paper} padding={2} gap={1}>
            <Typography variant="title3">Consumables</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Add stock items that crews should check during Make Ready, grouped
              by the pouch, drawer, cupboard, or area they belong in. For the
              'section', you can choose an existing section in this wizard or
              type a new one (i.e. "Top Drawer").
            </Typography>
            {consumables.map((item, index) => (
              <Grid container spacing={1} key={`consumable-${index}`}>
                <Grid item xs={12} lg={2.5}>
                  <Autocomplete
                    freeSolo
                    value={item.sectionName}
                    options={sectionOptions}
                    onChange={(e, newValue) =>
                      updateConsumable(index, { sectionName: newValue || '' })
                    }
                    onInputChange={(e, newInputValue) =>
                      updateConsumable(index, { sectionName: newInputValue })
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Section"
                        size="small"
                        fullWidth
                        required
                        error={
                          isConsumableStarted(item) &&
                          !String(item.sectionName || '').trim()
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} lg={3.5}>
                  <TextField
                    value={item.name}
                    onChange={(e) =>
                      updateConsumable(index, { name: e.target.value })
                    }
                    label="Item"
                    size="small"
                    fullWidth
                    required
                    error={
                      isConsumableStarted(item) &&
                      !String(item.name || '').trim()
                    }
                    helperText={
                      isConsumableStarted(item) &&
                      !String(item.name || '').trim()
                        ? 'Required'
                        : ''
                    }
                  />
                </Grid>
                <Grid item xs={12} lg={2}>
                  <TextField
                    value={item.size}
                    onChange={(e) =>
                      updateConsumable(index, { size: e.target.value })
                    }
                    label="Size"
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} lg={1.5}>
                  <TextField
                    value={item.quantity}
                    onChange={(e) =>
                      updateConsumable(index, { quantity: e.target.value })
                    }
                    label="Qty"
                    type="number"
                    size="small"
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} lg={2.5}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    gap={1}
                  >
                    <FormControlLabel
                      label="No expiry"
                      control={
                        <Checkbox
                          checked={item.noExpiry}
                          onChange={(e) =>
                            updateConsumable(index, {
                              noExpiry: e.target.checked,
                            })
                          }
                        />
                      }
                    />
                    <IconButton
                      aria-label="Delete consumable"
                      color="error"
                      onClick={() => deleteConsumable(index)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                </Grid>
              </Grid>
            ))}
            <Button
              variant="outlined"
              onClick={() =>
                setConsumables((prev) => [...prev, { ...blankConsumable }])
              }
            >
              Add Consumable
            </Button>
          </Stack>

          <Stack component={Paper} padding={2} gap={1}>
            <Typography variant="title3">Expected Assets</Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Add reusable equipment that should be present, such as monitors,
              bags, or immobilisation devices. Leave the sub-category blank to
              accept any asset in that category.
            </Typography>
            {requiredAssets.map((asset, index) => {
              const missingCategory =
                isAssetStarted(asset) && !String(asset.category || '').trim()
              return (
                <Grid container spacing={1} key={`asset-${index}`}>
                  <Grid item xs={12} lg={2}>
                    <TextField
                      value={asset.sectionName}
                      onChange={(e) =>
                        updateAsset(index, { sectionName: e.target.value })
                      }
                      label="Section"
                      size="small"
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <TextField
                      select
                      value={asset.category}
                      onChange={(e) =>
                        updateAsset(index, {
                          category: e.target.value,
                          subCategory: '',
                        })
                      }
                      label="Category"
                      size="small"
                      fullWidth
                      error={missingCategory}
                      helperText={missingCategory ? 'Required' : ''}
                    >
                      {Object.keys(assetCategories).map((category) => (
                        <MenuItem key={category} value={category}>
                          {category}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} lg={4}>
                    <TextField
                      select
                      value={asset.subCategory}
                      onChange={(e) =>
                        updateAsset(index, { subCategory: e.target.value })
                      }
                      label="Sub Category"
                      size="small"
                      fullWidth
                      disabled={!asset.category}
                      helperText={
                        asset.category && !asset.subCategory
                          ? `Any ${asset.category}`
                          : ''
                      }
                    >
                      <MenuItem value="">
                        <em>Any {asset.category || 'sub-category'}</em>
                      </MenuItem>
                      {(assetCategories[asset.category] || []).map(
                        (subCategory) => (
                          <MenuItem key={subCategory} value={subCategory}>
                            {subCategory}
                          </MenuItem>
                        ),
                      )}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} lg={2}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <TextField
                        value={asset.quantity}
                        onChange={(e) =>
                          updateAsset(index, { quantity: e.target.value })
                        }
                        label="Qty"
                        type="number"
                        size="small"
                        fullWidth
                      />
                      <IconButton
                        aria-label="Delete expected asset"
                        color="error"
                        onClick={() => deleteAsset(index)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Stack>
                  </Grid>
                </Grid>
              )
            })}
            <Button
              variant="outlined"
              onClick={() =>
                setRequiredAssets((prev) => [...prev, { ...blankAsset }])
              }
            >
              Add Expected Asset
            </Button>
            {incompleteAssets.length > 0 && (
              <Alert severity="warning">
                {incompleteAssets.length} expected asset row
                {incompleteAssets.length === 1 ? ' has' : 's have'} no category
                (row
                {incompleteAssets.length === 1 ? '' : 's'}{' '}
                {incompleteAssets.map(({ index }) => index + 1).join(', ')}).
                Pick a category or delete the row - it will not be saved
                otherwise.
              </Alert>
            )}
          </Stack>
        </Stack>
      )}

      {activeStep === 2 && (
        <Stack component={Paper} padding={2} gap={2}>
          <Alert severity="info">
            Assign this load list now so staff can scan or open the item
            directly from Make Ready.
          </Alert>
          {wildcardAssets.length > 0 && (
            <Alert severity="info">
              {wildcardAssets.length} expected asset
              {wildcardAssets.length === 1 ? '' : 's'} will accept any
              sub-category (
              {wildcardAssets.map((a) => `Any ${a.category}`).join(', ')}).
            </Alert>
          )}
          {incompleteAssets.length > 0 && (
            <Alert severity="warning">
              {incompleteAssets.length} expected asset row
              {incompleteAssets.length === 1 ? '' : 's'} on the Requirements
              step {incompleteAssets.length === 1 ? 'has' : 'have'} no category
              and will not be saved.
            </Alert>
          )}
          {isVehicleList ? (
            <Autocomplete
              multiple
              value={selectedVehicles}
              options={vehicles}
              onChange={(e, newValue) => setSelectedVehicles(newValue)}
              getOptionLabel={(option) =>
                `${option.callSign}${option.vehicleType ? ` (${option.vehicleType})` : ''}`
              }
              isOptionEqualToValue={(opt, val) => opt._id === val._id}
              renderInput={(params) => (
                <TextField {...params} label="Assign to Vehicles" />
              )}
            />
          ) : (
            <Autocomplete
              multiple
              value={selectedAssets}
              options={assets}
              onChange={(e, newValue) => setSelectedAssets(newValue)}
              getOptionLabel={(option) =>
                `${option.assetID}${option.category ? ` (${option.category})` : ''}`
              }
              isOptionEqualToValue={(opt, val) => opt._id === val._id}
              renderInput={(params) => (
                <TextField {...params} label="Assign to Assets" />
              )}
            />
          )}
        </Stack>
      )}

      <Box display="flex" justifyContent="space-between" gap={1}>
        <Button
          variant="outlined"
          disabled={activeStep === 0 || submitting}
          onClick={() => setActiveStep((prev) => prev - 1)}
        >
          Back
        </Button>
        {activeStep < 2 ? (
          <Button
            variant="contained"
            disabled={!canContinue}
            onClick={() => setActiveStep((prev) => prev + 1)}
          >
            Continue
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={submitting}
            onClick={handleComplete}
          >
            Complete Setup
          </Button>
        )}
      </Box>

      <Dialog
        open={confirmIncomplete}
        onClose={() => setConfirmIncomplete(false)}
      >
        <DialogTitle>Some expected assets will not be saved</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            These rows have no category and cannot be added to the list:
            <Box component="ul" sx={{ pl: 2.5, mt: 1, mb: 1 }}>
              {incompleteAssets.map(({ asset, index }) => (
                <li key={index}>
                  Row {index + 1}
                  {asset.sectionName ? ` in ${asset.sectionName}` : ''}
                  {asset.subCategory ? ` (${asset.subCategory})` : ''}
                </li>
              ))}
            </Box>
            Go back to fix them, or continue without them.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setConfirmIncomplete(false)
              setActiveStep(1)
            }}
          >
            Go back
          </Button>
          <Button variant="contained" onClick={submit}>
            Continue without them
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default MakeReadyWizard
